import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Logo } from "../components/site/Logo";
import { Check } from "lucide-react";

export default function Registrazione() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      setError("Devi accettare i Termini e Condizioni e la Privacy Policy per registrarti.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await register(form.name, form.email, form.password, accepted, form.phone);
      toast.success("Account creato con successo");
      navigate("/area");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Errore di registrazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white" data-testid="register-page">
      <div className="hidden w-1/2 flex-col justify-between bg-[#0A192F] p-12 lg:flex">
        <Logo dark />
        <div>
          <h2 className="font-display text-3xl font-bold leading-tight text-white">Crea il tuo account ViviTranquillo</h2>
          <p className="mt-4 text-slate-300">Attiva il Piano €49/anno e inizia a vivere senza pensieri.</p>
          <ul className="mt-8 space-y-3">
            {["3 richieste annuali", "Assistenza utenze e credito", "Area Clienti dedicata"].map((f) => (
              <li key={f} className="flex items-center gap-3 text-slate-300">
                <Check className="h-5 w-5 text-[#10B981]" /> {f}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-sm text-slate-500">© {new Date().getFullYear()} ViviTranquillo</p>
      </div>

      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="lg:hidden"><Logo /></div>
          <h1 className="mt-8 font-display text-3xl font-bold text-[#0A192F]">Registrati</h1>
          <p className="mt-2 text-slate-600">Bastano pochi secondi per iniziare.</p>

          {error && <div data-testid="register-error" className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#0A192F]">Nome e cognome</label>
              <input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-[#10B981]" />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#0A192F]">Email</label>
              <input data-testid="register-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-[#10B981]" />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#0A192F]">Numero di telefono</label>
              <input data-testid="register-phone" required type="tel" inputMode="tel" placeholder="es. 333 1234567" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-[#10B981]" />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#0A192F]">Password</label>
              <input data-testid="register-password" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-[#10B981]" />
            </div>
            <label className="flex items-start gap-2.5 text-sm text-slate-600">
              <input data-testid="register-accept-terms" type="checkbox" required checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[#10B981]" />
              <span>
                Accetto i{" "}
                <Link to="/termini" target="_blank" data-testid="register-terms-link" className="font-semibold text-[#10B981] hover:underline">Termini e Condizioni</Link>{" "}
                e la{" "}
                <Link to="/privacy" target="_blank" data-testid="register-privacy-link" className="font-semibold text-[#10B981] hover:underline">Privacy Policy</Link>
              </span>
            </label>
            <button data-testid="register-submit" disabled={loading || !accepted} className="w-full rounded-full bg-[#10B981] px-8 py-3.5 font-semibold text-white transition-colors hover:bg-[#059669] disabled:opacity-60">
              {loading ? "Creazione..." : "Crea account"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600">
            Hai già un account?{" "}
            <Link to="/login" data-testid="register-to-login" className="font-semibold text-[#10B981]">Accedi</Link>
          </p>
          <p className="mt-2 text-center text-sm">
            <Link to="/" className="text-slate-400 hover:text-[#0A192F]">← Torna alla home</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
